import React, {useState, useEffect, Fragment} from 'react'
import {useDispatch, useSelector} from "react-redux"
import DateFnsUtils from "@date-io/date-fns"

// Material-UI Core
import {makeStyles, withStyles} from '@material-ui/core/styles'
import Grid from '@material-ui/core/Grid'
import MenuItem from '@material-ui/core/MenuItem'
import TextField from '@material-ui/core/TextField'
import Typography from '@material-ui/core/Typography'
import Divider from '@material-ui/core/Divider'
import Button from '@material-ui/core/Button'
import Box from '@material-ui/core/Box'
import FormGroup from '@material-ui/core/FormGroup'
import FormControlLabel from '@material-ui/core/FormControlLabel'
import Checkbox from '@material-ui/core/Checkbox'

// Material-UI Element
import {MuiPickersUtilsProvider, KeyboardDatePicker} from "@material-ui/pickers"

// Custom Component
import {Alert} from '../Alert/Alert'
import ConfirmDialog from '../Dialog/ConfirmDialog'
import Legend from '../Legend/Legend'
import FileDropzone from '../FileDropzone/FileDropzone'
import CreateFormZoneContent from './CreateFormZoneContent'
import DocSubZoneContent from './DocSubZoneContent'
import WebViewer from "../WebViewer/WebViewer"

// Custom Style
import {redColor, oceanBlueColor, grayColor, blackColor, DodgerBlueColor} from '../../styles/jss/cmsMainStyle'
import {docSubCreateFormStyle} from '../../styles/jss/components/documentSubmission/docSubCreateFormStyle'

// Fetch Data
import {fileUpload} from '../../dataSource/fileManager'
import {getDocumentSubmissionType, getDocumentSubmissionForm, documentSubmissionCreate} from '../../dataSource/documentSubmission'


const FormTitleTypography = withStyles({
	root: {
		"fontSize": 15,
		"fontWeight": 600,
		"color": "#0000b3",
	},
}) (Typography)

const ReceiptTitleTypography = withStyles({
	root: {
		"fontSize": 20,
		"fontWeight": 800,
		"color": "#0000b3",
	},
}) (Typography)


const useStyles = makeStyles(theme => docSubCreateFormStyle(theme))

const DocSubCreateForm = props => {
	const classes = useStyles()
	const dispatch = useDispatch()
	const {submissionSearchAction} = props

	// Redux Data
	const documentSubmissionTypeRedux = useSelector(state => state.documentSubmissionTypeRedux)
	const documentSubmissionFormFieldRedux = useSelector(state => state.documentSubmissionFormFieldRedux)
	const documentSubmissionCreateValueRedux = useSelector(state => state.documentSubmissionCreateValueRedux)

	const [documentSubmissionType, setDocumentSubmissionType] = useState(null)
	const [submissionTypeValue, setSubmissionTypeValue] = useState(null)
	const [submissionTypeSeq, setSubmissionTypeSeq] = useState(null)
	const [submissionTypeName, setSubmissionTypeName] = useState(null)
	const [submissionFormSeq, setSubmissionFormSeq] = useState(null)
	const [submissionFormField, setSubmissionFormField] = useState(null)
	const [submissionCreateValue, setSubmissionCreateValue] = useState(null)
	const [receipt, setReceipt] = useState(null)
	const [validationErrors, setValidationErrors] = useState({})

	const [fileUploadingProperty, setFileUploadingProperty] = useState({})
	const [webViewProps, setWebViewProps] = useState(null)
	const [webViewerDocumentClose, setWebViewerDocumentClose] = useState(false)
	
	
	const [confirmDialogOpen, setConfirmDialogOpen] = useState(false)
	const [confirmDialogData, setConfirmDialogData] = useState({
		dialogTitle: "",
		dialogContent: "",
		confirmAction: "",
		cancelAction: ""
	})

	const [alertOpen, setAlertOpen] = useState(false)
	const [alertData, setAlertData] = useState({
		type: "",
		title: "",
		content: ""
	})


	// Build Confirm Dialog
	const confirmDialogHandleOpen = (data) => {
		setConfirmDialogData({
			dialogTitle: data.dialogTitle,
			dialogContent: data.dialogContent,
			confirmAction: data.confirmAction,
			cancelAction: data.cancelAction
		})

		setConfirmDialogOpen(true)
	}

	const confirmDialogHandleClose = () => {
		setConfirmDialogOpen(false)
	}


	// Build Alert
	const buildAlert = (data) => {
		setAlertData({
			type: data.type,
			title: data.title,
			content: data.content
		})
		setAlertOpen(true)
	}


	// Clear Data Storage
	const clearData = () => {
		setSubmissionTypeValue(null)
		setSubmissionTypeSeq(null)
		setSubmissionTypeName(null)
		setSubmissionFormSeq(null)
		setSubmissionFormField(null)
		setSubmissionCreateValue(null)
		
		dispatch({
			type: "ADD_DOCUMENT_SUBMISSION_FORM_FIELD",
			data: null
		})

		dispatch({
			type: "ADD_DOCUMENT_SUBMISSION_CREATE_VALUE",
			data: null
		})
	}

	const clearReceipt = () => {
		setReceipt(null)
	}


	// Handle Form Input
	const handleFieldChange = event => {
		const {name, value} = event.target
		const res = {...submissionCreateValue.values}

		res[name] = value
		setSubmissionCreateValue({...submissionCreateValue, values: res})
		dispatch({
			type: "ADD_DOCUMENT_SUBMISSION_CREATE_VALUE",
			data: {...submissionCreateValue, values: res}
		})

		validate({...submissionCreateValue, values: res})
	}


	const handleCheckboxChange = elementName => event => {
		const {name, checked} = event.target
		const res = {...submissionCreateValue.values}

		if (res[elementName]) {
			var tempArray = res[elementName].split(",")
		}
		else {
			var tempArray = []
		}

		if (checked) {
			tempArray.push(name)
			res[elementName] = tempArray.toString()
			
			setSubmissionCreateValue({...submissionCreateValue, values: res})
			dispatch({
				type: "ADD_DOCUMENT_SUBMISSION_CREATE_VALUE",
				data: {...submissionCreateValue, values: res}
			})

		}
		else {
			if (tempArray.indexOf(name) > -1) {
				tempArray.splice(tempArray.indexOf(name), 1)
				res[elementName] = tempArray.toString()

				setSubmissionCreateValue({...submissionCreateValue, values: res})
				dispatch({
					type: "ADD_DOCUMENT_SUBMISSION_CREATE_VALUE",
					data: {...submissionCreateValue, values: res}
				})
			}
		}

		validate({...submissionCreateValue, values: res})
	}


	const handleDateFieldChange = (date, name) => {
		const res = {...submissionCreateValue.values}

		res[name] = date
		setSubmissionCreateValue({...submissionCreateValue, values: res})
		dispatch({
			type: "ADD_DOCUMENT_SUBMISSION_CREATE_VALUE",
			data: {...submissionCreateValue, values: res}
		})

		validate({...submissionCreateValue, values: res})
	}


	// Handle Submission Type Select
	const handleSubmissionTypeSelect = event => {
		const {value} = event.target
		const typeSeq = event.currentTarget.getAttribute("data-typeseq")
		const typeName = event.currentTarget.getAttribute("data-typename")
		const formSeq = event.currentTarget.getAttribute("data-formseq")
		let subValue = {}
		let values = {}
		let files = {}

		clearReceipt()
		clearData()
		setValidationErrors({})
		setSubmissionTypeValue(value)
		setSubmissionTypeSeq(typeSeq)
		setSubmissionTypeName(typeName)
		setSubmissionFormSeq(formSeq)

		setFileUploadingProperty({})
		dispatch({
			type: "ADD_FILE_UPLOADING_PROPERTY",
			data: null
		})

		getDocumentSubmissionForm(formSeq).then(res => {
			setSubmissionFormField(res.data)
			dispatch({
				type: "ADD_DOCUMENT_SUBMISSION_FORM_FIELD",
				data: res.data
			})

			res.data.map(element => {
				if (element.type != "files_upload") {
					values[element.name] = ""
				}
				else {
					files[element.name] = []
				}
			})

			subValue.type_seq = typeSeq
			subValue.values = values
			subValue.files = files

			setSubmissionCreateValue(subValue)
			dispatch({
				type: "ADD_DOCUMENT_SUBMISSION_CREATE_VALUE",
				data: subValue
			})

			// setFileUploadingProperty({})
			// dispatch({
			// 	type: "ADD_FILE_UPLOADING_PROPERTY",
			// 	data: null
			// })
		})
	}


	// Main Action
	const fileUploadAction = (fileType, acceptedFilesPool) => {
		let UploadingSource = buildFileUploadingProperty(fileType, acceptedFilesPool)

		acceptedFilesPool.map(file => {
			fileUpload(file).then(res => {
				console.log("*****************************************************************************")
				console.log("Temporary upload File Successful")
				console.log(res)
				console.log("*****************************************************************************")

				UploadingSource = removeFileUploadingProperty(UploadingSource, fileType, file)
				buildFileRecord(fileType, res.data[0])
			})
		})
	}

	const buildFileUploadingProperty = (fileType, fileUploadRecord) => {
		const res = {...submissionCreateValue.files}

		for (var key in res) {
			res[key] = []
		}

		res[fileType] = [...fileUploadRecord]

		setFileUploadingProperty(res)
		dispatch({
			type: "ADD_FILE_UPLOADING_PROPERTY",
			data: res
		})

		return res
	}

	const buildFileRecord = (fileType, fileUploadRecord) => {
		const val = {...submissionCreateValue.files}
		fileUploadRecord.modifydate = ""

		val[fileType].push(fileUploadRecord)

		setSubmissionCreateValue({...submissionCreateValue, files: val})
		dispatch({
			type: "ADD_DOCUMENT_SUBMISSION_CREATE_VALUE",
			data: {...submissionCreateValue, files: val}
		})

		validate({...submissionCreateValue, files: val})
	}
	
	const removeFileUploadingProperty = (sourceRecords, fileType, key) => {
		if (sourceRecords) {
			const res = {...sourceRecords}
			res[fileType] = sourceRecords[fileType].filter(sourceRecord => sourceRecord !== key)

			setFileUploadingProperty(res)
			dispatch({
				type: "ADD_FILE_UPLOADING_PROPERTY",
				data: res
			})

			return res
		}
	}


	const removeFileConfirmAction = (fileType, fileSeq) => {
		confirmDialogHandleOpen({
			dialogTitle: "Document Submission file remove",
			dialogContent: "Do you want to remove the file.",
			confirmAction: () => removeFileAction(fileType, fileSeq),
			cancelAction: removeFileCancelAction
		})
	}

	const removeFileCancelAction = () => {
		confirmDialogHandleClose()
	}

	const removeFileAction = (fileType, fileSeq) => {
		confirmDialogHandleClose()
		const val = {...submissionCreateValue.files}

		val[fileType].map((res, index) => {
			if (res.file_seq == fileSeq) {
				val[fileType].splice(index, 1)

				setSubmissionCreateValue({...submissionCreateValue, files: val})
				dispatch({
					type: "ADD_DOCUMENT_SUBMISSION_CREATE_VALUE",
					data: {...submissionCreateValue, files: val}
				})

				console.log("*****************************************************************************")
				console.log("System Module: Document Submission")
				console.log("Message: File remove successful.")
				console.log(res)
				console.log("*****************************************************************************")

				setWebViewerDocumentClose(res.file_seq)
			}
		})
	}


	const submissionCreateConfirmAction = (params) => {
		if (validate()) {
			confirmDialogHandleOpen({
				dialogTitle: "Document Submission",
				dialogContent: "Do you want to send out this Submission.",
				confirmAction: () => submissionCreateAction(params),
				cancelAction: submissionCreateCancelAction
			})
		}
	}

	const submissionCreateCancelAction = () => {
		confirmDialogHandleClose()
	}


	const submissionCreateAction = params => {
		submissionCreateCancelAction()

		documentSubmissionCreate(params).then(res => {

			console.log("*****************************************************************************")
			console.log("System Module: Document Submission")
			console.log("Message: Document Submission Create successful.")
			console.log(res)
			console.log("*****************************************************************************")

			clearData()
			setReceipt(res.data[0])
			submissionSearchAction()

			buildAlert({
				// Alert Type: success, info, warning, error
				type: "success",
				title: "Success Alert",
				content: "The Document Submission have been sent"
			})
		})
	}


	const viewFileAction = (fileCat, fileSeq, filename) => {
		setWebViewProps({
			file_cat: fileCat,
			file_ref: fileSeq,
			file_name: filename,
			from: "API",
			autoLoading: false,
		})
	}


	// Input Validation
	const validate = (fieldValues = submissionCreateValue) => {
		let temp = {...validationErrors}

		submissionFormField.map(res => {
			if (res.mandatory) {
				if (res.type !== "files_upload") {
					if (res.name in fieldValues.values) {
						temp[res.name] = fieldValues.values[res.name] ? "" : "This field is required"
					}
				}
				else {
					if (res.name in fieldValues.files) {
						temp[res.name] = fieldValues.files[res.name].length > 0 ? "" : "This field is required"
					}
				}
			}
		})

		setValidationErrors({...temp})

		if (fieldValues == submissionCreateValue) {
			return Object.values(temp).every(x => x == "")
		}
	}


	useEffect(() => {
		getDocumentSubmissionType().then(res => {
			setDocumentSubmissionType(res.data)
			dispatch({
				type: "ADD_DOCUMENT_SUBMISSION_TYPE",
				data: res.data
			})
		})
	}, [])


	return (
		// console.log("Document Submission Type | Variable: documentSubmissionType (DocSubCreateForm.js)"),
		// console.log(documentSubmissionType),

		// console.log("Document Submission Type (Redux) | Variable: documentSubmissionTypeRedux (DocSubCreateForm.js)"),
		// console.log(documentSubmissionTypeRedux),

		// console.log("Document Submission Type Value | Variable: submissionTypeValue (DocSubCreateForm.js)"),
		// console.log(submissionTypeValue),

		// console.log("Document Submission Type Seq | Variable: submissionTypeSeq (DocSubCreateForm.js)"),
		// console.log(submissionTypeSeq),

		// console.log("Document Submission Type Name | Variable: submissionTypeName (DocSubCreateForm.js)"),
		// console.log(submissionTypeName),

		// console.log("Document Submission Form Seq | Variable: submissionFormSeq (DocSubCreateForm.js)"),
		// console.log(submissionFormSeq),

		console.log("Document Submission Form Field | Variable: submissionFormField (DocSubCreateForm.js)"),
		console.log(submissionFormField),

		// console.log("Document Submission Form Field (Redux) | Variable: documentSubmissionFormFieldRedux (DocSubCreateForm.js)"),
		// console.log(documentSubmissionFormFieldRedux),

		console.log("Document Submission Create Value | Variable: submissionCreateValue (DocSubCreateForm.js)"),
		console.log(submissionCreateValue),

		console.log("Document Submission File Uploading Property | Variable: fileUploadingProperty (DocSubCreateForm.js)"),
		console.log(fileUploadingProperty),

		// console.log("Validation Errors | Variable: validationErrors (DocSubCreateForm.js)"),
		// console.log(validationErrors),

		// console.log("Document Submission Create Value (Redux) | Variable: documentSubmissionCreateValueRedux (DocSubCreateForm.js)"),
		// console.log(documentSubmissionCreateValueRedux),

		console.log("---------------------------------------------------------------------------------"),


		<Fragment>
			<Alert
				open={alertOpen}
				alertType={alertData.type}
				alertTitle={alertData.title}
				alertContent={alertData.content}
				handleClose={() => setAlertOpen(false)}
			/>

			<ConfirmDialog
				open={confirmDialogOpen}
				dialogTitle={confirmDialogData.dialogTitle}
				dialogContent={confirmDialogData.dialogContent}
				confirmAction={confirmDialogData.confirmAction}
				cancelAction={confirmDialogData.cancelAction}
			/>

			<div className={classes.marginSpace}></div>

			<Grid container spacing={1} className={classes.content}>
				<Grid item xs={12} sm={6} lg={4}>

					<div className={classes.submissinTypeRoot}>
						<TextField
							select
							required
							fullWidth
							key={"documentSubmissionType"}
							id={"documentSubmissionType"}
							name={"documentSubmissionType"}
							label={"Document Submission Type"}
							size="small"
							variant="outlined"
							value={submissionTypeValue || ""}
							onChange={handleSubmissionTypeSelect}
						>
							{
								documentSubmissionType !== null ?
									documentSubmissionType.map(opt => (
										<MenuItem 
											key={opt.type_seq} 
											value={opt.type_code} 
											data-formseq={opt.form_seq}
											data-typeseq={opt.type_seq}
											data-typename={opt.type_name}
										>
											{opt.type_name}
										</MenuItem>
									))
								: <MenuItem key={"init"} value={null}><em>None</em></MenuItem>
							}
						</TextField>
					</div>

					<Divider className={classes.divider} />

					{
						receipt !== null ?
							<div>
								<div className={classes.receiptSubject}>Document Submission Receipt</div>

								<div className={classes.receiptTitle}>Submission Number: </div>
								<div className={classes.receiptContent}><ReceiptTitleTypography variant="button">{receipt.subm_code}</ReceiptTitleTypography></div>

								<div className={classes.receiptTitle}>Company: </div>
								<div className={classes.receiptContent}>{receipt.company.company_name}</div>

								<div className={classes.receiptTitle}>Document Submission Type: </div>
								<div className={classes.receiptContent}>{receipt.type.type_name}</div>

								<div className={classes.receiptTitle}>Submission Date: </div>
								<div className={classes.receiptContent}>{receipt.createdate}</div>
							</div>
						:
							<div>
								<FormTitleTypography variant="button">{submissionTypeName}</FormTitleTypography>
								<div className={classes.fieldDivision}>
									<Grid container spacing={2} className={classes.content}>
									{
										submissionFormField !== null ?
											submissionFormField.map(element => {
												switch (element.type) {
													case "text":
														return (
															<Grid item xs={12} sm={12} lg={12}>
																<TextField
																	key={element.id}
																	id={element.id}
																	name={element.name}
																	label={element.display_name}
																	required={element.mandatory}
																	type="text"
																	fullWidth={true}
																	size="small"
																	variant="outlined"
																	value={submissionCreateValue !== null ? submissionCreateValue.values[element.name] ? submissionCreateValue.values[element.name] : element.value : ""}
																	onChange={handleFieldChange}
																	error={validationErrors[element.name] && validationErrors[element.name] !== "" ? true : false}
																	helperText={validationErrors[element.name]}
																/>
															</Grid>
														)
													
													case "select":
														return (
															<Grid item xs={12} sm={12} lg={12}>
																<TextField
																	select
																	key={element.id}
																	id={element.id}
																	name={element.name}
																	label={element.display_name}
																	required={element.mandatory}
																	fullWidth={true}
																	size="small"
																	variant="outlined"
																	value={submissionCreateValue !== null ? submissionCreateValue.values[element.name] ? submissionCreateValue.values[element.name] : element.value : ""}
																	onChange={handleFieldChange}
																	error={validationErrors[element.name] && validationErrors[element.name] !== "" ? true : false}
																	helperText={validationErrors[element.name]}
																>
																	{
																		element.childs.map(opt => (
																			<MenuItem key={opt.value} value={opt.value}>{opt.display_name}</MenuItem>
																		))
																	}
																</TextField>
															</Grid>
														)

													case "checkbox":
														return (
															<Grid item xs={12} sm={12} lg={12}>
																<div key={element.id}>
																	<Box 
																		border={1}
																		borderRadius="borderRadius"
																		borderColor={element.mandatory ? validationErrors[element.name] && validationErrors[element.name] ? "error.main" : "grey.400" : "grey.400"}
																		className={classes.checkboxArea}
																	>
																		<Typography 
																			variant="button"
																			color={element.mandatory ? validationErrors[element.name] && validationErrors[element.name] ? "error" : "textPrimary" : "textPrimary"}
																		>
																			{element.mandatory ? element.display_name + " *" : element.display_name}
																		</Typography>

																		<FormGroup row={true}>
																			{
																				element.childs.map((cbox, index) => {
																					return (
																						<FormControlLabel
																							key={index}
																							control={
																								<Checkbox
																									onChange={handleCheckboxChange(element.name)}
																									name={cbox.name}
																									color="primary"
																								/>
																							}
																							label={cbox.display_name}
																						/>
																					)
																				})
																			}
																		</FormGroup>
																	</Box>
																	{
																		element.mandatory ? 
																			validationErrors[element.name] && validationErrors[element.name] ?	
																				<Typography variant="caption" color="error" className={classes.requireErrorMsg}>{validationErrors[element.name]}</Typography> 
																			: "" 
																		: ""
																	}
																</div>
															</Grid>
														)

													case "textarea":
														return (
															<Grid item xs={12} sm={12} lg={12}>
																<TextField
																	multiline
																	rows={4}
																	key={element.id}
																	id={element.id}
																	name={element.name}
																	label={element.display_name}
																	required={element.mandatory}
																	fullWidth={true}
																	size="small"
																	variant="outlined"
																	value={submissionCreateValue !== null ? submissionCreateValue.values[element.name] ? submissionCreateValue.values[element.name] : element.value : ""}
																	onChange={handleFieldChange}
																	error={validationErrors[element.name] && validationErrors[element.name] !== "" ? true : false}
																	helperText={validationErrors[element.name]}
																/>
															</Grid>
														)

													case "datepicker": 
														return (
															<Grid item xs={12} sm={12} lg={12}>
																<MuiPickersUtilsProvider utils={DateFnsUtils} key={element.id}>
																	<KeyboardDatePicker
																		id={element.id}
																		name={element.name}
																		label={element.display_name}
																		required={element.mandatory}
																		format="dd/MM/yyyy"
																		placeholder="DD/MM/YYYY"
																		size="small"
																		inputVariant="outlined"
																		KeyboardButtonProps={{
																			"aria-label": "change date"
																		}}
																		className={classes.datePickerField}
																		value={submissionCreateValue !== null ? submissionCreateValue.values[element.name] !== "" ? submissionCreateValue.values[element.name] ? submissionCreateValue.values[element.name] : element.value : null : null}
																		onChange={date => handleDateFieldChange(date, element.name)}
																		error={validationErrors[element.name] && validationErrors[element.name] !== "" ? true : false}
																		helperText={validationErrors[element.name]}
																	/>
																</MuiPickersUtilsProvider>
															</Grid>
														)
													
													case "files_upload":
														return (
															<Grid item xs={12} sm={12} lg={12}>
																<Legend
																	id={element.id}
																	// label={element.mandatory ? element.display_name + " *" : element.display_name}
																	label={element.display_name}
																	required={element.mandatory}
																	normalTitleColor={grayColor[10]}
																	normalBorderColor={grayColor[4]}
																	messageTitleColor={redColor[6]}
																	messageBorderColor={redColor[6]}
																	
																	error={validationErrors[element.name] && validationErrors[element.name] !== "" ? true : false}
																	helperText={validationErrors[element.name]}
																>
																	<FileDropzone
																		key={element.id}
																		title={element.mandatory ? element.display_name + " *" : element.display_name}
																		fileType={element.name}
																		required={element.mandatory}
																		disabled={false}
																		fileUploadAction={fileUploadAction}
																		viewFileAction={viewFileAction}
																		fileRemoveAction={removeFileConfirmAction}
																		DropzoneContent={DocSubZoneContent}
																		// openFileManagerDialog={fileManagerDialogHandleOpen}

																		
																	/>
																</Legend>
															</Grid>
														)
												}
											})
										: ""
									}
									</Grid>
								</div>
								{
									submissionFormField !== null ?
										<div className={classes.buttonMenu}>
											<Button variant="contained" color="primary" onClick={() => submissionCreateConfirmAction(submissionCreateValue)}>Send</Button>
										</div>
									: ""
								}
							</div>
							
					}
				</Grid>

				<Grid item xs={12} sm={6} lg={8}>
					<WebViewer 
						property={webViewProps}             // Pass the parameter to display document in Viewer.  Parameters => {file_cat: integer, file_ref: integer, file_name: String, autoLoading: Boolean} or null
						readOnly={true}                     // Hide all annotation tools.  Parameters => Boolean
						zoom={false}                        // Zoom the document.  Parameters => integer (Percentage) or false
						move={false}                        // Scroll the view area of document to X Y.  Parameters => {x: integer, y: integer} or false
						ocr={false}                         // Display OCR Button on Viewer.  Parameters => Boolean
						closeDoc={webViewerDocumentClose}   // Close Viewer document.   Parameters => Boolean
					/>
				</Grid>
			</Grid>



		</Fragment>
	)
}


export default DocSubCreateForm